<template>
  <div class="max-w-md mx-auto w-full">
    <div v-if="error" class="mb-6 px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">
      {{ error }}
    </div>

    <form @submit.prevent="createProject()">
      <div class="space-y-6">
        <!-- Form fields with consistent styling -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Project name</label>
          <input
            type="text"
            v-model="value.name"
            class="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
            placeholder="Dunder Mifflin"
            required
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Subdomain</label>
          <input
            type="text"
            v-model="value.url"
            :class="{
              'border-red-300 focus:ring-red-500': domainAvailable === false
            }"
            class="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:border-transparent transition-all"
            placeholder="dunder-mifflin"
            required
          />
          
          <transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="transform -translate-y-1 opacity-0"
            enter-to-class="transform translate-y-0 opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="transform translate-y-0 opacity-100"
            leave-to-class="transform -translate-y-1 opacity-0"
          >
            <div v-if="domainAvailable === false" class="mt-2 text-sm text-red-600">
              This subdomain is already taken. Please choose another one.
            </div>
          </transition>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">Brand color</label>
          <div class="relative">
            <div
              class="w-full p-2 border border-gray-200 rounded-xl cursor-pointer flex items-center"
              @click.stop="showColorPicker = !showColorPicker"
            >
              <div 
                class="w-6 h-6 rounded-lg mr-2 flex items-center justify-center text-white text-xs font-medium"
                :style="{ backgroundColor: colors.hex }"
              >
                Aa
              </div>
              <span class="text-gray-700">{{ colors.hex }}</span>
            </div>
            
            <div 
              v-if="showColorPicker" 
              class="absolute z-10 mt-2 rounded-xl shadow-lg overflow-hidden"
              v-click-outside="() => showColorPicker = false"
            >
              <chrome-picker 
                :value="colors"
                @input="updateColor"
              />
            </div>
          </div>
        </div>

         <!-- File upload with consistent styling -->
         <FileUpload
          label="Logo image (optional)"
          accept="image/*"
          v-model="value.logo"
          class="border border-dashed border-gray-200 rounded-xl p-4 text-sm"
        />

        <!-- Submit button -->
        <button
          type="submit"
          :disabled="!formValid || isLoading"
          class="w-full bg-[#F54239] min-h-[48px] text-white py-3 px-4 rounded-xl font-medium hover:brightness-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span v-if="!isLoading" class="flex items-center justify-center">
            Continue
            <simple-svg :filepath="NextIcon" class="ml-2 w-4 h-4" />
          </span>
          <simple-svg v-else :filepath="LoaderIcon" class="animate-spin w-5 h-5 mx-auto" />
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import FileUpload from "./file-upload";
import NextIcon from "../images/icons/arrow-right.svg";
import LoaderIcon from "../images/icons/loader.svg";
import { Chrome } from 'vue-color'

export default {
  props: ["value"],

  data() {
    return {
      formValid: false,
      domainAvailable: null,
      isLoading: false,
      NextIcon,
      LoaderIcon,
      error: "",
      showColorPicker: false,
      colors: {
        hex: '#F54239',
        hsl: { h: 4, s: 0.91, l: 0.59 },
        hsv: { h: 4, s: 0.73, v: 0.96 },
        rgba: { r: 245, g: 66, b: 57, a: 1 },
      },
    };
  },

  components: {
    FileUpload,
    'chrome-picker': Chrome,
  },

  methods: {
    checkSubdomain: debounce(function () {
      Rails.ajax({
        url: `/projects/check_subdomain?subdomain=${this.inputSubdomain}`,
        type: "GET",
        beforeSend: function () {
          return true;
        },
        success: (data) => {
          this.domainAvailable = data.domain_available;
        },
        error: (data, status) => {
          console.log(data);
        },
      });
    }, 500),

    createProject() {
      this.isLoading = true;
      let formData = new FormData();
      formData.append("project[name]", this.value.name);
      formData.append("project[subdomain]", this.value.url);
      formData.append("project[color]", this.colors.hex);

      if (this.value.logo) {
        formData.append("project[logo]", this.value.logo);
      }

      Rails.ajax({
        url: `/projects`,
        type: "POST",
        data: formData,
        dataType: "multipart/form-data",
        beforeSend: function () {
          return true;
        },
        success: (data) => {
          if (typeof fbq !== 'undefined') {
            fbq("track", "StartTrial", {
              value: "29.00",
              currency: "USD",
              predicted_ltv: "290.00",
            });
          }
          this.value.id = data.id;
          this.$emit("submit");
        },
        error: (data, status) => {
          console.log(data);
          if (data.error)
            this.error = data.error;

          this.isLoading = false;
        },
      });
    },

    checkFormValid() {
      if (
        this.value.name.length > 0 &&
        this.value.url.length > 0 &&
        this.domainAvailable
      ) {
        this.formValid = true;
      } else {
        this.formValid = false;
      }
    },

    slugify: function (text) {
      text = text.toString().toLowerCase().trim();

      const sets = [
        { to: "a", from: "[ÀÁÂÃÄÅÆĀĂĄẠẢẤẦẨẪẬẮẰẲẴẶ]" },
        { to: "c", from: "[ÇĆĈČ]" },
        { to: "d", from: "[ÐĎĐÞ]" },
        { to: "e", from: "[ÈÉÊËĒĔĖĘĚẸẺẼẾỀỂỄỆ]" },
        { to: "g", from: "[ĜĞĢǴ]" },
        { to: "h", from: "[ĤḦ]" },
        { to: "i", from: "[ÌÍÎÏĨĪĮİỈỊ]" },
        { to: "j", from: "[Ĵ]" },
        { to: "ij", from: "[Ĳ]" },
        { to: "k", from: "[Ķ]" },
        { to: "l", from: "[ĹĻĽŁ]" },
        { to: "m", from: "[Ḿ]" },
        { to: "n", from: "[ÑŃŅŇ]" },
        { to: "o", from: "[ÒÓÔÕÖØŌŎŐỌỎỐỒỔỖỘỚỜỞỠỢǪǬƠ]" },
        { to: "oe", from: "[Œ]" },
        { to: "p", from: "[ṕ]" },
        { to: "r", from: "[ŔŖŘ]" },
        { to: "s", from: "[ßŚŜŞŠ]" },
        { to: "t", from: "[ŢŤ]" },
        { to: "u", from: "[ÙÚÛÜŨŪŬŮŰŲỤỦỨỪỬỮỰƯ]" },
        { to: "w", from: "[ẂŴẀẄ]" },
        { to: "x", from: "[ẍ]" },
        { to: "y", from: "[ÝŶŸỲỴỶỸ]" },
        { to: "z", from: "[ŹŻŽ]" },
        { to: "-", from: "[·/_,:;']" },
      ];

      sets.forEach((set) => {
        text = text.replace(new RegExp(set.from, "gi"), set.to);
      });

      text = text
        .toString()
        .toLowerCase()
        .replace(/\s+/g, "-") // Replace spaces with -
        .replace(/&/g, "-and-") // Replace & with 'and'
        .replace(/[^\w\-]+/g, "") // Remove all non-word chars
        .replace(/\--+/g, "-") // Replace multiple - with single -
        .replace(/^-+/, "") // Trim - from start of text
        .replace(/-+$/, ""); // Trim - from end of text

      return text;
    },

    updateColor(colors) {
      this.colors = colors;
      this.$emit('input', { ...this.value, color: colors.hex });
    },
  },

  computed: {
    projectName() {
      return this.value.name;
    },

    inputSubdomain() {
      return this.value.url;
    },
  },

  watch: {
    value: {
      deep: true,

      handler() {
        this.checkFormValid();
      },
    },

    projectName: function (newName) {
      this.value.url = this.slugify(newName);
    },

    inputSubdomain: function (domain) {
      if (domain.length > 0) {
        this.checkSubdomain();
      } else {
        this.domainAvailable = null;
      }
    },

    domainAvailable: function () {
      this.checkFormValid();
    },
  },

  directives: {
    clickOutside: {
      bind(el, binding) {
        el.clickOutsideEvent = function(event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.addEventListener('click', el.clickOutsideEvent);
      },
      unbind(el) {
        document.removeEventListener('click', el.clickOutsideEvent);
      },
    },
  },
};
</script>

<style scoped>
.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.slideDown-enter,
.slideDown-leave-to {
  opacity: 0;
  transform: translateY(-0.5rem);
}

.slideDown-enter-to,
.slideDown-leave {
  opacity: 1;
  transform: translateY(0);
}

.slideDown-enter-active,
.slideDown-leave-active {
  @apply transition duration-150 ease-in;
}

.spinning {
  animation: spin 2000ms infinite cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
